import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, CircleAlert, CircleCheck, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/i18n/use-locale'
import { siteContact } from '@/i18n/site-contact'
import { serviceSummaries } from '@/content/services-index'

interface FormValues {
    name: string
    email: string
    company: string
    country: string
    phone: string
    service: string
    message: string
    preferredTime: string
    consent: boolean
    website: string
}

type FieldName = keyof FormValues
type FormErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'pending' | 'success' | 'error'

const initialValues: FormValues = {
    name: '',
    email: '',
    company: '',
    country: '',
    phone: '',
    service: '',
    message: '',
    preferredTime: '',
    consent: false,
    website: ''
}

const fieldOrder: Array<FieldName> = ['name', 'email', 'company', 'country', 'phone', 'service', 'message', 'consent']

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE = /^[+()\d\s./-]{6,40}$/

const inputClass = 'block w-full rounded-xl border border-input bg-background px-4 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground/80 hover:border-foreground/25 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-destructive/15 disabled:opacity-60'

interface FieldProps {
    id: string
    label: string
    required?: boolean
    optionalLabel?: string
    error?: string
    hint?: string
    className?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, required = false, optionalLabel, error, hint, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='block text-sm font-medium text-foreground'>
                {label}
                {required && <span className='text-destructive' aria-hidden='true'> *</span>}
                {optionalLabel && <span className='font-normal text-muted-foreground'>{` (${optionalLabel})`}</span>}
            </label>

            <div className='relative mt-2'>
                {children}
            </div>

            {hint && !error && (
                <p id={`${id}-hint`} className='mt-1.5 text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${id}-error`} className='mt-1.5 text-xs font-medium text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`
    return undefined
}

/** Consultation request form with client-side validation, a honeypot and pending, success and error states. */
export const ContactForm: FC = (): ReactNode => {
    const locale = useLocale()
    const copy = siteContact[locale]
    const f = copy.form
    const [values, setValues] = useState<FormValues>(initialValues)
    const [errors, setErrors] = useState<FormErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [failure, setFailure] = useState('')
    const successRef = useRef<HTMLDivElement>(null)

    const services = [
        ...serviceSummaries.map(item => ({ value: item.slug, label: item.name[locale] })),
        ...copy.options.extraServices
    ]

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const validate = (data: FormValues): FormErrors => {
        const found: FormErrors = {}
        if (data.name.trim().length < 2) found.name = f.errors.name
        if (!EMAIL.test(data.email.trim())) found.email = f.errors.email
        if (data.company.trim().length < 2) found.company = f.errors.company
        if (!data.country) found.country = f.errors.country
        if (data.phone.trim() !== '' && !PHONE.test(data.phone.trim())) found.phone = f.errors.phone
        if (!data.service) found.service = f.errors.service
        if (data.message.trim().length < 10) found.message = f.errors.message
        if (!data.consent) found.consent = f.errors.consent
        return found
    }

    const update = (field: FieldName) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = event.target
        const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors(current => {
                const next = { ...current }
                delete next[field]
                return next
            })
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (status === 'pending') return

        const found = validate(values)
        setErrors(found)

        const first = fieldOrder.find(field => found[field])

        if (first) {
            if (status === 'error') setStatus('idle')
            document.getElementById(`contact-${first}`)?.focus()
            return
        }

        setStatus('pending')
        setFailure('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: values.name.trim(),
                    email: values.email.trim(),
                    company: values.company.trim(),
                    country: values.country,
                    phone: values.phone.trim(),
                    service: values.service,
                    message: values.message.trim(),
                    preferredTime: values.preferredTime,
                    consent: values.consent,
                    website: values.website,
                    locale
                })
            })

            if (!response.ok) {
                const data = await response.json().catch(() => null) as { code?: string | null } | null
                setFailure(data?.code === 'rate_limited' ? f.rateLimited : f.failure)
                setStatus('error')
                return
            }

            setValues(initialValues)
            setStatus('success')
        }
        catch {
            setFailure(f.failure)
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='flex flex-col items-start rounded-3xl border border-border bg-card p-8 outline-none sm:p-10'
            >
                <span className='flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700'>
                    <CircleCheck className='size-6' aria-hidden='true' />
                </span>

                <h2 className='mt-6 font-display text-4xl leading-tight'>
                    {f.successTitle}
                </h2>

                <p className='mt-3 text-lg leading-relaxed text-muted-foreground'>
                    {f.successText}
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 inline-flex h-11 items-center rounded-full border border-border px-5 text-sm font-medium transition-colors hover:bg-mist focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                >
                    {f.again}
                </button>
            </div>
        )
    }

    const pending = status === 'pending'
    const hasErrors = Object.keys(errors).length > 0

    return (
        <form noValidate onSubmit={onSubmit} className='relative rounded-3xl border border-border bg-card p-6 shadow-xl shadow-ink/5 sm:p-10' aria-labelledby='contact-form-title'>
            <h2 id='contact-form-title' className='font-display text-3xl leading-tight sm:text-4xl'>
                {f.title}
            </h2>

            <p className='mt-2 text-sm text-muted-foreground'>
                {f.intro}
            </p>

            <div className='mt-8 grid gap-5 sm:grid-cols-2'>
                <Field id='contact-name' label={f.name} required error={errors.name}>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={describedBy('contact-name', errors.name)}
                        className={cn(inputClass, 'h-12')}
                    />
                </Field>

                <Field id='contact-email' label={f.email} required error={errors.email}>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('contact-email', errors.email)}
                        className={cn(inputClass, 'h-12')}
                    />
                </Field>

                <Field id='contact-company' label={f.company} required error={errors.company} hint={f.companyHint}>
                    <input
                        id='contact-company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        value={values.company}
                        onChange={update('company')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.company)}
                        aria-describedby={describedBy('contact-company', errors.company, f.companyHint)}
                        className={cn(inputClass, 'h-12')}
                    />
                </Field>

                <Field id='contact-country' label={f.country} required error={errors.country}>
                    <select
                        id='contact-country'
                        name='country'
                        value={values.country}
                        onChange={update('country')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.country)}
                        aria-describedby={describedBy('contact-country', errors.country)}
                        className={cn(inputClass, 'h-12 appearance-none pr-10', !values.country && 'text-muted-foreground')}
                    >
                        <option value=''>{f.countryPlaceholder}</option>
                        {copy.options.countries.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                </Field>

                <Field id='contact-phone' label={f.phone} optionalLabel={f.optional} error={errors.phone}>
                    <input
                        id='contact-phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        value={values.phone}
                        onChange={update('phone')}
                        disabled={pending}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('contact-phone', errors.phone)}
                        className={cn(inputClass, 'h-12')}
                    />
                </Field>

                <Field id='contact-service' label={f.service} required error={errors.service}>
                    <select
                        id='contact-service'
                        name='service'
                        value={values.service}
                        onChange={update('service')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.service)}
                        aria-describedby={describedBy('contact-service', errors.service)}
                        className={cn(inputClass, 'h-12 appearance-none pr-10', !values.service && 'text-muted-foreground')}
                    >
                        <option value=''>{f.servicePlaceholder}</option>
                        {services.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                </Field>

                <Field id='contact-message' label={f.message} required error={errors.message} className='sm:col-span-2'>
                    <textarea
                        id='contact-message'
                        name='message'
                        rows={5}
                        value={values.message}
                        onChange={update('message')}
                        disabled={pending}
                        placeholder={f.messagePlaceholder}
                        aria-required='true'
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('contact-message', errors.message)}
                        className={cn(inputClass, 'min-h-36 resize-y py-3 leading-relaxed')}
                    />
                </Field>

                <Field id='contact-preferredTime' label={f.time} optionalLabel={f.optional} className='sm:col-span-2'>
                    <select
                        id='contact-preferredTime'
                        name='preferredTime'
                        value={values.preferredTime}
                        onChange={update('preferredTime')}
                        disabled={pending}
                        className={cn(inputClass, 'h-12 appearance-none pr-10', !values.preferredTime && 'text-muted-foreground')}
                    >
                        <option value=''>{f.timePlaceholder}</option>
                        {copy.options.times.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                </Field>
            </div>

            <div className='absolute -left-[10000px] top-auto size-px overflow-hidden' aria-hidden='true'>
                <label htmlFor='contact-website'>{f.honeypot}</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            <div className='mt-6'>
                <div className='flex items-start gap-3'>
                    <input
                        id='contact-consent'
                        name='consent'
                        type='checkbox'
                        checked={values.consent}
                        onChange={update('consent')}
                        disabled={pending}
                        aria-required='true'
                        aria-invalid={Boolean(errors.consent)}
                        aria-describedby={errors.consent ? 'contact-consent-error' : undefined}
                        className='mt-0.5 size-5 shrink-0 rounded border-input accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    />

                    <p className='text-sm leading-relaxed text-foreground/85'>
                        <label htmlFor='contact-consent'>
                            {f.consentBefore}
                        </label>
                        {' '}
                        <Link href='/privacy' target='_blank' rel='noopener' className='font-medium text-primary underline underline-offset-4'>
                            {f.consentLink}
                        </Link>
                        {f.consentAfter}
                        <span className='text-destructive' aria-hidden='true'> *</span>
                    </p>
                </div>

                {errors.consent && (
                    <p id='contact-consent-error' className='ml-8 mt-1.5 text-xs font-medium text-destructive'>
                        {errors.consent}
                    </p>
                )}
            </div>

            {(hasErrors || status === 'error') && (
                <div role='alert' className='mt-6 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {status === 'error' && failure ? failure : f.errorSummary}
                </div>
            )}

            <button
                type='submit'
                disabled={pending}
                aria-busy={pending}
                className='mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ink px-7 text-[0.95rem] font-medium text-white transition-colors hover:bg-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-80 sm:w-auto'
            >
                {pending
                    ? <LoaderCircle className='size-4 animate-spin motion-reduce:animate-none' aria-hidden='true' />
                    : <Send className='size-4' aria-hidden='true' />}
                {pending ? f.pending : f.submit}
            </button>
        </form>
    )
}
